import {
    getGridBooleanOperators,
    getGridDateOperators,
    getGridNumericOperators,
    getGridStringOperators,
    GridFilterInputValue,
    GridFilterModel,
    GridFilterOperator,
    GridValidRowModel,
    GridValueFormatterParams,
    GridValueGetterParams,
} from '@mui/x-data-grid-premium';
import { IFilter, LogicalFilter, Operator } from '@price-for-profit/micro-services';
import { ApprovalStatusEnum } from 'shared/constants';
import { GridColumns } from 'shared/types';
import { BackendFilterModel } from 'types/backend';
import { formatFourDecimalMoney, formatMoney, formatPercent, isValueEmpty } from './formats';

// The grid hands us its own MUI-shaped filter model (field/operator/logicOperator);
// the backend's GridFilterModel DTO still expects the legacy columnField/operatorValue/linkOperator names.
export function toBackendFilterModel(filterModel: GridFilterModel): BackendFilterModel {
    return {
        linkOperator: filterModel.logicOperator === 'or' ? 'or' : 'and',
        items: filterModel.items
            .filter((item) => !!item.value)
            .map((item) => ({
                id: typeof item.id === 'number' ? item.id : undefined,
                columnField: item.field,
                operatorValue: item.operator,
                value: item.value,
            })),
    };
}

export const moneyFormatter = (params: GridValueFormatterParams<number>) =>
    params.value === null || params.value === undefined ? '' : formatMoney(params.value);
export const moneyFourDecimalFormatter = (params: GridValueFormatterParams<number>) =>
    params.value === null || params.value === undefined ? '' : formatFourDecimalMoney(params.value);
export const percentFormatter = (params: GridValueFormatterParams<number>) =>
    params.value === null || params.value === undefined ? '' : formatPercent(params.value);
export const getStatusColumnColor = (status: string | ApprovalStatusEnum) => {
    switch (status) {
        case ApprovalStatusEnum.NEEDSAPPROVAL:
            return 'warning';
        case ApprovalStatusEnum.REJECTED:
            return 'error';
        case ApprovalStatusEnum.APPROVED:
        default:
            return 'success';
    }
};

export function removeUnimplementedFilters<T>(columns: GridColumns<T>): GridColumns<T> {
    return columns.map((column) => {
        switch (column.type) {
            case 'number':
                return {
                    ...column,
                    filterOperators: getGridNumericOperators().filter((operator) =>
                        ['=', '!=', '>', '>=', '<', '<='].includes(operator.value)
                    ),
                };
            case 'boolean':
                return {
                    ...column,
                    filterOperators: getGridBooleanOperators().filter((operator) => ['is'].includes(operator.value)),
                };
            case 'date':
                return {
                    ...column,
                    filterOperators: getGridDateOperators().filter((operator) =>
                        ['is', 'not', 'after', 'onOrAfter', 'before', 'onOrBefore'].includes(operator.value)
                    ),
                };
            case 'dateTime':
                return {
                    ...column,
                    filterOperators: getGridDateOperators().filter((operator) =>
                        ['onOrAfter', 'before'].includes(operator.value)
                    ),
                };
            default:
                return {
                    ...column,
                    filterOperators: implementedStringOperators,
                };
        }
    });
}

const doesNotContainFilter: GridFilterOperator = {
    InputComponent: GridFilterInputValue,
    getApplyFilterFn: (filterItem, column) => {
        if (!filterItem.field || !filterItem.value || !filterItem.operator) {
            return null;
        }

        return (params): boolean => {
            const rowValue = column.valueGetter ? column.valueGetter(params as GridValueGetterParams) : params.value;
            return !rowValue.toString().includes(filterItem.value);
        };
    },
    value: 'does not contain',
    label: 'does not contain',
};

const implementedStringOperators = [
    ...getGridStringOperators().filter((operator) =>
        ['contains', 'equals', 'startsWith', 'endsWith'].includes(operator.value)
    ),
    doesNotContainFilter,
];

//RULES
type ValidateValue<T> = string | GridFilterModel | T | undefined;
export const isEmpty = (value: any) => {
    return value === undefined || value === null || value === '';
};

export function isPositiveOrZero(value: any) {
    if (typeof value !== 'string') return true;
    if (isValueEmpty(value) || value === undefined) return true;
    return parseInt(value) >= 0 || 'Must be a positive number';
}

export function isBetweenNeg1AndInclusivePos1<T>(value: ValidateValue<T>) {
    if (typeof value !== 'string') return true;
    if (isEmpty(value) || value === undefined) return true;
    const numValue = parseFloat(value);
    return (numValue > -1 && numValue <= 1) || 'Must be between -100% and 100%';
}

export function isBetweenInclusiveNeg1andInclusivePos1<T>(value: ValidateValue<T>) {
    if (typeof value !== 'string') return true;
    if (isEmpty(value) || value === undefined) return true;
    const numValue = parseFloat(value);
    return (numValue >= -1 && numValue <= 1) || 'Must be between -100% and 100% inclusively';
}

export function isValidDate(value: any) {
    return value.toString().toLowerCase() !== 'invalid date' || 'Must be a valid date';
}

export function isNotPastDate(value: any) {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const valueDate = new Date(value);
    valueDate.setHours(0, 0, 0, 0);
    return valueDate >= currentDate || 'Past dates are not allowed';
}

export function is2WeeksOrLess(value: any) {
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    // set to 2 weeks from now
    currentDate.setDate(currentDate.getDate() + 14);

    const valueDate = new Date(value);
    valueDate.setHours(0, 0, 0, 0);
    return valueDate <= currentDate || 'Date must be within 2 weeks';
}

export function generateCollectionFilter<T extends GridValidRowModel>(
    filterModel: BackendFilterModel,
    baseFilter?: IFilter<T>
): IFilter<T> | undefined {
    if (!filterModel.items.length) return baseFilter;

    const filters = filterModel.items.reduce<IFilter<T>[]>((result, filter) => {
        if (filter.operatorValue && filter.value) {
            const newFilter: IFilter<T> = {
                property: filter.columnField as keyof T,
                operator: convertOperatorDataGridToDas(filter.operatorValue),
                value: filter.value,
            };
            result.push(newFilter);
        }
        return result;
    }, []);

    const gridLogicalFilter: LogicalFilter<T> = {
        logicalOperator: filterModel.linkOperator || 'and',
        filters: filters,
    };

    if (baseFilter) {
        return {
            logicalOperator: 'and',
            filters: [baseFilter, gridLogicalFilter],
        };
    }

    return gridLogicalFilter;
}

export function convertOperatorDataGridToDas(operator: string): Operator {
    // Dev Note / TODO
    // "lt" | "lte" only makes a difference depending on if your calling the lookup or doc service
    // "gt" | "gte" only makes a difference depending on if your calling the lookup or doc service

    switch (operator) {
        case 'equals':
        case 'is':
        case '=':
            return 'eq';
        case 'after':
        case '>':
            return 'gt';
        case 'onOrAfter':
        case '>=':
            return 'gte';
        case 'before':
        case '<':
            return 'lt';
        case 'onOrBefore':
        case '<=':
            return 'lte';
        case '!=':
        case 'not':
            return 'ne';
        case 'contains':
            return 'contains';
        case 'startsWith':
            return 'startswith';
        case 'endsWith':
            return 'endswith';
        case 'isEmpty':
            return 'isnull';
        case 'isNotEmpty':
            return 'isnotnull';
        case 'isAnyOf':
            return 'in';
        default:
            throw new Error(`filter operator [${operator}] is not supported`);
    }
}
